import React, { useState } from 'react';
import { Badge, Button } from '../ui';
import { useWindowSize } from '../../hooks/useWindowSize';

function initials(name = '') {
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';
}

const AVATAR_COLORS = ['#2A6E7F', '#1f93e0', '#3a8fa3', '#1a5568', '#0f6e56'];
function avatarColor(name = '') {
  if (!name) return AVATAR_COLORS[0];
  return AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];
}

/**
 * Ricava un label leggibile dal servizio prenotato.
 * Supporta sia la vecchia struttura (serviceSelected) che la nuova (celle / servizioLabel).
 */
function getServiceLabel(order) {
  // Nuova struttura — prenotazione da mappa
  if (order.celle?.length) {
    const labels = [...new Set(order.celle.map(c => c.servizioLabel || c.type).filter(Boolean))];
    const count = order.celle.length;
    return `${count}× ${labels.join(', ')}`;
  }
  // Vecchia struttura — prenotazione manuale legacy
  if (order.serviceSelected) {
    return `${order.quantity || 1}× ${order.serviceSelected}`;
  }
  return '—';
}

/**
 * Ricava un riepilogo date leggibile.
 * Mostra le prime 3 date + eventuale conteggio extra.
 */
function getDatesLabel(order) {
  const dates = order.selectedDate || [];
  if (!dates.length) return '—';
  const shown = dates.slice(0, 3).join(', ');
  return dates.length > 3 ? `${shown} +${dates.length - 3}` : shown;
}

export function BookingItem({ order, onCancel, onConfirm, showActions = true }) {
  const [expanded, setExpanded] = useState(false);
  const { isMobile } = useWindowSize();

  const name = order.guestName || (order.source === 'admin_manual' ? 'Prenotazione manuale' : 'Utente app');
  const serviceLabel = getServiceLabel(order);
  const datesLabel = getDatesLabel(order);
  const firstDate = order.selectedDate?.[0] || '—';

  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      {/* Riga principale */}
      <div onClick={() => setExpanded(!expanded)} style={{
        display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px',
        padding: isMobile ? '10px 0' : '12px 0', cursor: 'pointer',
        minHeight: '44px',
      }}>
        <div style={{
          width: isMobile ? '30px' : '34px', height: isMobile ? '30px' : '34px', borderRadius: '50%',
          background: avatarColor(name),
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '11px', fontWeight: 500, flexShrink: 0,
        }}>
          {initials(name)}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13.5px', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {name}
          </div>
          <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginTop: '1px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            {serviceLabel}
            <Badge status={order.status} />
          </div>
        </div>

        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: '13.5px', fontWeight: 500, color: 'var(--sand)' }}>
            {order.totalPrice ? `€ ${order.totalPrice}` : '—'}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '1px' }}>
            {firstDate}
          </div>
        </div>

        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{
          transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s',
          color: 'var(--text-muted)', flexShrink: 0,
        }}>
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>

      {/* Dettagli espansi */}
      {expanded && (
        <div style={{ padding: isMobile ? '0 0 12px 0' : '0 0 14px 46px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{
            display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '8px',
            background: 'var(--surface)', borderRadius: 'var(--radius-md)',
            padding: isMobile ? '10px' : '12px', fontSize: '12px',
          }}>
            <DetailRow label="ID ordine" value={order.id?.slice(0, 10) + '…'} />
            <DetailRow label="Servizio" value={serviceLabel} />
            <DetailRow label="Totale" value={order.totalPrice ? `€ ${order.totalPrice}` : '—'} />
            <DetailRow label="Date" value={datesLabel} />
            {order.guestPhone && <DetailRow label="Telefono" value={order.guestPhone} />}
            {order.notes && <DetailRow label="Note" value={order.notes} />}
            {order.dateType && <DetailRow label="Tipo" value={order.dateType} />}
            {order.celle?.length > 0 && (
              <DetailRow label="Posti" value={order.celle.map(c => c.id).join(', ')} />
            )}
          </div>

          {showActions && order.status !== 'cancelled' && (
            <div style={{ display: 'flex', gap: '8px' }}>
              {order.status === 'pending' && (
                <Button size="sm" variant="primary" onClick={() => onConfirm?.(order.id)}>
                  ✓ Conferma
                </Button>
              )}
              <Button size="sm" variant="danger" onClick={() => onCancel?.(order.id)}>
                Cancella
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div>
      <span style={{ color: 'var(--text-muted)', display: 'block', fontSize: '10.5px', marginBottom: '2px' }}>{label}</span>
      <span style={{ color: 'var(--text-soft)' }}>{value}</span>
    </div>
  );
}

export function BookingList({ orders, onCancel, onConfirm, emptyMessage = 'Nessuna prenotazione' }) {
  if (!orders?.length) {
    return (
      <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-muted)', fontSize: '13px' }}>
        🏖️ {emptyMessage}
      </div>
    );
  }
  return (
    <div>
      {orders.map(order => (
        <BookingItem key={order.id} order={order} onCancel={onCancel} onConfirm={onConfirm} />
      ))}
    </div>
  );
}